import React from 'react';
import { AlertTriangle, ExternalLink, Settings, Copy } from 'lucide-react';
import { configError } from '../firebase';

export function FirebaseSetupWarning() {
  const getErrorMessage = () => {
    switch (configError) {
      case 'SERVICE_ACCOUNT_DETECTED':
        return {
          title: 'Clave de Cuenta de Servicio Detectada',
          description: 'Has configurado FIREBASE_CONFIG con un JSON de Cuenta de Servicio. El SDK del navegador requiere una "Configuración de Aplicación Web".',
          steps: [
            'Ve a Firebase Console > Configuración del proyecto > General.',
            'En "Tus aplicaciones", busca tu App Web (o crea una con el icono </>).',
            'Copia el objeto "firebaseConfig" (el que tiene apiKey, authDomain, appId, etc.).',
            'Pégalo en el secreto FIREBASE_CONFIG de AI Studio.'
          ]
        };
      case 'MISSING_API_KEY':
        return {
          title: 'Falta apiKey en la Configuración',
          description: 'Tu configuración de Firebase no tiene una clave de API válida.',
          steps: [
            'Asegúrate de haber copiado el fragmento de código completo de Firebase Console.',
            'El JSON debe incluir "apiKey", "authDomain", "projectId", etc.'
          ]
        };
      case 'MISSING_CONFIG':
        return {
          title: 'Firebase no está configurado',
          description: 'No se ha encontrado la variable de entorno FIREBASE_CONFIG.',
          steps: [
            'Ve al menú Settings > Secrets en AI Studio.',
            'Añade un secreto llamado FIREBASE_CONFIG.',
            'Pega el JSON de configuración de tu aplicación web de Firebase.'
          ]
        };
      case 'INVALID_JSON':
        return {
          title: 'Error de Formato en FIREBASE_CONFIG',
          description: 'El valor de FIREBASE_CONFIG no es un JSON válido.',
          steps: [
            'Asegúrate de copiar SOLO el objeto de configuración (el que empieza por { y termina por }).',
            'Si copiaste el fragmento de código completo de Firebase, asegúrate de no incluir las etiquetas <script> o la asignación de variable (p. ej., const firebaseConfig = ...).',
            'Todas las claves deben estar entre comillas dobles (p. ej., "apiKey": "..." en lugar de apiKey: "...").',
            'No uses comillas simples (\') para envolver las claves o valores.'
          ]
        };
      default:
        return {
          title: 'Error de Configuración de Firebase',
          description: 'La configuración de Firebase es inválida o ha fallado la inicialización.',
          steps: [
            'Revisa la consola del navegador (F12) para más detalles.',
            'Asegúrate de que el JSON en FIREBASE_CONFIG sea válido.'
          ]
        };
    }
  };

  const error = getErrorMessage();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden border border-red-100">
        <div className="bg-red-50 p-6 flex items-center gap-4 border-b border-red-100">
          <div className="p-3 bg-red-100 rounded-full">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-red-900">{error.title}</h2>
            <p className="text-red-700 font-medium mt-1">{error.description}</p>
          </div>
        </div>
        
        <div className="p-8 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-500" />
              Pasos para solucionar:
            </h3>
            <ol className="space-y-3">
              {error.steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-gray-700">
                  <span className="flex-shrink-0 w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Ejemplo de configuración correcta:</h4>
            <pre className="text-xs font-mono text-gray-800 bg-white p-4 rounded-lg border border-gray-200 overflow-x-auto">
{`{
  "apiKey": "AIzaSy...",
  "authDomain": "proyecto.firebaseapp.com",
  "projectId": "proyecto-id",
  "storageBucket": "proyecto.appspot.com",
  "messagingSenderId": "...",
  "appId": "1:..."
}`}
            </pre>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <a 
              href="https://console.firebase.google.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-lg shadow-indigo-200"
            >
              Ir a Firebase Console
              <ExternalLink className="w-4 h-4" />
            </a>
            <button 
              onClick={() => window.location.reload()}
              className="flex-1 flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-900 font-bold py-3 px-6 rounded-xl border-2 border-gray-200 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 text-center border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Una vez configurado, la aplicación se cargará automáticamente.
          </p>
        </div>
      </div>
    </div>
  );
}
